import type { PublicIndexDoc, PublicPropertyDoc } from '@blauturquesa/shared/public-view';
import { options, usingEmulators } from '../../firebase/config';

// Lectura de les vistes públiques (§3.1) amb l'API REST de Firestore, sense el SDK: obrir un QR amb
// dades mòbils ha de continuar sent ràpid (§10.4). Cada document és una lectura, com amb el SDK.
// Les regles permeten llegir publicProperties i publicIndex a tothom.

const base = usingEmulators
  ? `http://127.0.0.1:8080/v1/projects/${options.projectId}/databases/(default)/documents`
  : `https://firestore.googleapis.com/v1/projects/${options.projectId}/databases/(default)/documents`;

type Value = Record<string, unknown>;

/** Valor de l'API REST → valor JS. */
export function decodeValue(v: Value): unknown {
  if ('nullValue' in v) return null;
  if ('booleanValue' in v) return v['booleanValue'];
  if ('integerValue' in v) return Number(v['integerValue']);
  if ('doubleValue' in v) return Number(v['doubleValue']);
  if ('stringValue' in v) return v['stringValue'];
  if ('timestampValue' in v) return Date.parse(String(v['timestampValue']));
  if ('arrayValue' in v) return ((v['arrayValue'] as { values?: Value[] }).values ?? []).map(decodeValue);
  if ('mapValue' in v) return decodeFields((v['mapValue'] as { fields?: Record<string, Value> }).fields ?? {});
  return null;
}

export function decodeFields(fields: Record<string, Value>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, decodeValue(v)]));
}

// Memòria curta: de la fitxa al slider i tornada no es torna a llegir. Caduca al cap d'un minut,
// perquè un agent que acaba d'editar la casa en vegi els canvis.
const TTL_MS = 60_000;
const cache = new Map<string, { at: number; value: Promise<unknown> }>();

async function getDoc<T>(path: string): Promise<T | null> {
  const url = `${base}/${path}${options.apiKey && !usingEmulators ? `?key=${encodeURIComponent(options.apiKey)}` : ''}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`firestore ${res.status}`);
  const body = (await res.json()) as { fields?: Record<string, Value> };
  return decodeFields(body.fields ?? {}) as T;
}

function cached<T>(path: string): Promise<T | null> {
  const hit = cache.get(path);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.value as Promise<T | null>;
  const p = getDoc<T>(path);
  // Un error no es queda a la memòria: el proper intent torna a provar.
  p.catch(() => cache.delete(path));
  cache.set(path, { at: Date.now(), value: p });
  return p;
}

export const getPublicProperty = (slug: string) => cached<PublicPropertyDoc>(`publicProperties/${encodeURIComponent(slug)}`);
export const getPublicIndex = () => cached<PublicIndexDoc>('publicIndex/home');
